import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import { FillerStats } from '../../../services/api.service';
import { SpeakerLabelIndex, resolveLabelMeta } from '../utils/voice-analytics.utils';
import { AnalyticsCard } from './meeting-analytics-panel.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type Props = {
  fillerStats: FillerStats | null;
  labelIndex: SpeakerLabelIndex;
};

/** Задача H1: карточка «Слова-паразиты» — total/per_minute/top_words по спикеру. */
const FillerStatsCard: React.FC<Props> = ({ fillerStats, labelIndex }) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!fillerStats) return [];
    return Object.entries(fillerStats)
      .map(([label, stat], i) => ({
        meta: resolveLabelMeta(label, labelIndex, i),
        total: stat.total || 0,
        perMinute: stat.per_minute || 0,
        topWords: stat.top_words || [],
      }))
      .filter(row => row.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [fillerStats, labelIndex]);

  if (!rows.length) return null;

  const leaderLabel = rows.length > 1 ? rows[0].meta.label : null;

  return (
    <AnalyticsCard title={t('voice-analytics.filler-title')} badge="data">
      <div className={cn('voice-filler-list')}>
        {rows.map(row => (
          <div key={row.meta.label} className={cn('voice-filler-row')}>
            <div className={cn('voice-filler-head')}>
              <span className={cn('voice-filler-dot')} style={{ backgroundColor: row.meta.color }} />
              <span className={cn('voice-filler-name')} title={row.meta.displayName}>
                {row.meta.shortName}
              </span>
              {row.meta.label === leaderLabel && (
                <span className={cn('voice-filler-leader')}>{t('voice-analytics.filler-leader')}</span>
              )}
              <span className={cn('voice-filler-stats')}>
                <span className={cn('voice-filler-stat-value')}>{row.total}</span>
                <span className={cn('voice-filler-stat-label')}>{t('voice-analytics.filler-total')}</span>
                <span className={cn('voice-filler-stat-sep')}>·</span>
                <span className={cn('voice-filler-stat-value')}>{row.perMinute}</span>
                <span className={cn('voice-filler-stat-label')}>{t('voice-analytics.filler-per-minute')}</span>
              </span>
            </div>
            {row.topWords.length > 0 && (
              <div className={cn('voice-filler-chips')}>
                {row.topWords.map(word => (
                  <span key={word} className={cn('voice-filler-chip')}>{word}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </AnalyticsCard>
  );
};

export default FillerStatsCard;
